"use client";

import {
  ElementType,
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Wysiwyg } from "../ui/Wysiwyg";

interface TextTypeProps {
  className?: string;
  showCursor?: boolean;
  hideCursorWhileTyping?: boolean;
  cursorCharacter?: string | React.ReactNode;
  cursorBlinkDuration?: number;
  cursorClassName?: string;
  text: string | string[];
  as?: ElementType;
  typingSpeed?: number;
  initialDelay?: number;
  pauseDuration?: number;
  deletingSpeed?: number;
  loop?: boolean;
  textColors?: string[];
  variableSpeed?: { min: number; max: number };
  onSentenceComplete?: (sentence: string, index: number) => void;
  startOnVisible?: boolean;
  reverseMode?: boolean;
}

const TextType = ({
  text,
  as: Component = "div",
  typingSpeed = 50,
  initialDelay = 0,
  pauseDuration = 2000,
  deletingSpeed = 30,
  loop = true,
  className = "",
  showCursor = true,
  hideCursorWhileTyping = false,
  cursorCharacter = "|",
  cursorClassName = "",
  cursorBlinkDuration = 0.5,
  textColors = [],
  variableSpeed,
  onSentenceComplete,
  startOnVisible = false,
  reverseMode = false,
  ...props
}: TextTypeProps & React.HTMLAttributes<HTMLElement>) => {
  const [displayedText, setDisplayedText] = useState("");
  const [currentCharIndex, setCurrentCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(!startOnVisible);
  const containerRef = useRef<HTMLElement>(null);

  const textArray = useMemo(
    () => (Array.isArray(text) ? text : [text]),
    [text]
  );

  const getRandomSpeed = useCallback(() => {
    if (!variableSpeed) return typingSpeed;
    const { min, max } = variableSpeed;
    return Math.random() * (max - min) + min;
  }, [variableSpeed, typingSpeed]);

  const getCurrentTextColor = useCallback(() => {
    if (textColors.length === 0) return "inherit";
    return textColors[currentTextIndex % textColors.length];
  }, [textColors, currentTextIndex]);

  useEffect(() => {
    if (!startOnVisible || !containerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [startOnVisible]);

  useEffect(() => {
    if (!isVisible) return;

    let timeout: ReturnType<typeof setTimeout>;

    const currentText = textArray[currentTextIndex];

    const getTypingUnits = (text: string) => {
      const units: string[] = [];
      // This regex matches <tag>, &entity;, or any single character
      const regex = /<[^>]*>|&[a-zA-Z0-9#]+;|./g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        units.push(match[0]);
      }
      return units;
    };

    const units = getTypingUnits(currentText);
    const processedUnits = reverseMode ? [...units].reverse() : units;

    const executeTypingAnimation = () => {
      if (isDeleting) {
        if (displayedText === "") {
          setIsDeleting(false);
          if (currentTextIndex === textArray.length - 1 && !loop) {
            return;
          }

          if (onSentenceComplete) {
            onSentenceComplete(textArray[currentTextIndex], currentTextIndex);
          }

          setCurrentTextIndex((prev) => (prev + 1) % textArray.length);
          setCurrentCharIndex(0);
          timeout = setTimeout(() => {}, pauseDuration);
        } else {
          timeout = setTimeout(() => {
            // Remove the last "unit" (tag, entity, or char)
            const unitRegex = /<[^>]*>|&[a-zA-Z0-9#]+;|.$/;
            const match = displayedText.match(unitRegex);
            if (match) {
              setDisplayedText((prev) => prev.slice(0, -match[0].length));
              setCurrentCharIndex((prev) => prev - 1);
            } else {
              setDisplayedText("");
              setCurrentCharIndex(0);
            }
          }, deletingSpeed);
        }
      } else {
        if (currentCharIndex < processedUnits.length) {
          timeout = setTimeout(
            () => {
              setDisplayedText(
                (prev) => prev + processedUnits[currentCharIndex]
              );
              setCurrentCharIndex((prev) => prev + 1);
            },
            variableSpeed ? getRandomSpeed() : typingSpeed
          );
        } else if (textArray.length >= 1) {
          if (!loop && currentTextIndex === textArray.length - 1) return;
          timeout = setTimeout(() => {
            setIsDeleting(true);
          }, pauseDuration);
        }
      }
    };

    if (currentCharIndex === 0 && !isDeleting && displayedText === "") {
      timeout = setTimeout(executeTypingAnimation, initialDelay);
    } else {
      executeTypingAnimation();
    }

    return () => clearTimeout(timeout);
  }, [
    currentCharIndex,
    displayedText,
    isDeleting,
    typingSpeed,
    deletingSpeed,
    pauseDuration,
    textArray,
    currentTextIndex,
    loop,
    initialDelay,
    isVisible,
    reverseMode,
    variableSpeed,
    onSentenceComplete,
    getCurrentTextColor,
    textArray,
    getRandomSpeed,
  ]);

  const units = useMemo(() => {
    const currentText = textArray[currentTextIndex];
    const getTypingUnits = (text: string) => {
      const result: string[] = [];
      const regex = /<[^>]*>|&[a-zA-Z0-9#]+;|./g;
      let match;
      while ((match = regex.exec(text)) !== null) {
        result.push(match[0]);
      }
      return result;
    };
    return getTypingUnits(currentText || "");
  }, [textArray, currentTextIndex]);

  const shouldHideCursor =
    hideCursorWhileTyping && (currentCharIndex < units.length || isDeleting);

  const textColor = getCurrentTextColor();

  return (
    <Component
      ref={containerRef}
      className={`flex flex-wrap items-baseline tracking-tight ${className}`}
      {...props}
    >
      <Wysiwyg
        className="inline whitespace-pre-wrap"
        style={{ color: textColor || "inherit" }}
        html={displayedText}
        mode="raw"
      />
      {showCursor && (
        <span
          // CSS, not a GSAP `repeat: -1` tween: that kept GSAP's ticker awake at
          // display refresh on every page and outlived the component.
          className={`ml-1 inline-block animate-blink ${shouldHideCursor ? "hidden" : ""} ${cursorClassName}`}
          style={{ animationDuration: `${cursorBlinkDuration * 2}s` }}
        >
          {cursorCharacter}
        </span>
      )}
    </Component>
  );
};

export default TextType;
